/**
 * features/status/systemEventLog.ts
 *
 * Lightweight ring buffer of the last 10 significant system events.
 * Written to by the orchestration layer and AI subsystem.
 * Read by the SystemStatusBar for display.
 *
 * No logic changes — purely observational.
 */

export type SystemEventCategory =
  | 'market'    // candle, tick, fetch
  | 'detection' // BOS, CHOCH, sweep, VWAP, regime
  | 'ai'        // AI refresh triggered, completed, failed
  | 'thesis'    // thesis updated, invalidated
  | 'ranking'   // ranking recalculated
  | 'alert'     // alert emitted
  | 'feed';     // feed connected, disconnected

export interface SystemEvent {
  id:        string;
  ts:        number;        // Unix ms
  category:  SystemEventCategory;
  symbol:    string | null;
  message:   string;        // human-readable one-liner
  detail?:   string;        // optional detail (trigger reason, hash, etc.)
}

const MAX_EVENTS = 20;
let eventLog: SystemEvent[] = [];
const listeners = new Set<() => void>();

function notify() { listeners.forEach(fn => fn()); }

export function subscribeEventLog(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getEventLog(): SystemEvent[] {
  return [...eventLog];
}

export function logEvent(
  category: SystemEventCategory,
  message:  string,
  symbol:   string | null = null,
  detail?:  string
): void {
  const evt: SystemEvent = {
    id:       `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    ts:       Date.now(),
    category,
    symbol,
    message,
    detail,
  };
  eventLog.unshift(evt);
  if (eventLog.length > MAX_EVENTS) eventLog = eventLog.slice(0, MAX_EVENTS);
  notify();
}

export function clearEventLog(): void {
  eventLog = [];
  notify();
}
